import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const SiteFooter = lazy(() => import('@/components/SiteFooter'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_SERVICES = ['Fashion Photography', 'Street Photography', 'Event Photography', 'Car Photography'];
const pageReveal = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8 } };
const successReveal = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.4 } };

const inputClass = "w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors";

const SuccessMessage = ({ onReset }) => (
  <motion.div className="border border-white/10 p-12 text-center" {...successReveal} data-testid="contact-success">
    <Send className="w-10 h-10 mx-auto mb-6 text-white/60" />
    <h2 className="text-2xl font-normal mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Enquiry Sent</h2>
    <p className="text-gray-400 text-sm mb-8">Thank you for reaching out. I'll be in touch shortly.</p>
    <button onClick={onReset} className="bg-white text-black hover:bg-gray-200 rounded-none px-8 py-3 text-sm uppercase tracking-widest transition-all duration-300" data-testid="send-another-btn">Send Another Enquiry</button>
  </motion.div>
);

const CustomField = ({ field, value, onChange }) => {
  const label = <label className="block text-sm uppercase tracking-widest mb-2">{field.label}</label>;
  if (field.field_type === 'dropdown') {
    return (
      <div>
        {label}
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} className={`${inputClass} appearance-none`} data-testid={`contact-custom-${field.id}`}>
          <option value="" disabled>Select {field.label.toLowerCase()}</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (field.field_type === 'textarea') {
    return (
      <div>
        {label}
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} rows={4} placeholder={`Enter ${field.label.toLowerCase()}`} className={`${inputClass} resize-none`} data-testid={`contact-custom-${field.id}`} />
      </div>
    );
  }
  return (
    <div>
      {label}
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} placeholder={`Enter ${field.label.toLowerCase()}`} className={inputClass} data-testid={`contact-custom-${field.id}`} />
    </div>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', service: '', enquiry: '' });
  const [customValues, setCustomValues] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, fieldsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/form-fields`),
      ]);
      setServiceOptions(catRes.data.map((c) => `${c.name} Photography`));
      setCustomFields(fieldsRes.data);
    } catch { setServiceOptions(FALLBACK_SERVICES); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleCustomChange = (fieldId, label, value) => {
    setCustomValues({ ...customValues, [fieldId]: { label, value } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const customFieldsPayload = {};
    Object.values(customValues).forEach(({ label, value }) => {
      if (value) customFieldsPayload[label] = value;
    });
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        custom_fields: Object.keys(customFieldsPayload).length > 0 ? customFieldsPayload : null,
      });
      toast.success('Enquiry sent successfully!');
      setSent(true);
      setForm({ name: '', email: '', service: '', enquiry: '' });
      setCustomValues({});
    } catch { toast.error('Failed to send enquiry. Please try again.'); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <Toaster position="top-right" theme="dark" />
      <motion.div className="max-w-2xl mx-auto" {...pageReveal}>
        <h1 className="text-4xl md:text-6xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="contact-heading">Get In Touch</h1>
        <p className="text-gray-400 mb-12 text-sm tracking-wide">Fill out the form below and I'll get back to you as soon as possible.</p>
        {sent ? (
          <SuccessMessage onReset={() => setSent(false)} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Name</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" className={inputClass} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Email Address</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" className={inputClass} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Service</label>
              <select name="service" value={form.service} onChange={handleChange} required className={`${inputClass} appearance-none`} data-testid="contact-service-select">
                <option value="" disabled>Select a service</option>
                {serviceOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            {customFields.map((field) => (
              <CustomField
                key={field.id}
                field={field}
                value={customValues[field.id]?.value || ''}
                onChange={(val) => handleCustomChange(field.id, field.label, val)}
              />
            ))}
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Your Enquiry</label>
              <textarea name="enquiry" value={form.enquiry} onChange={handleChange} required rows={6} placeholder="Tell me about what you're looking for..." className={`${inputClass} resize-none`} data-testid="contact-enquiry-input" />
            </div>
            <button type="submit" disabled={sending} className="w-full bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3" data-testid="contact-submit-btn">
              <Send size={16} />{sending ? 'Sending...' : 'Send Enquiry'}
            </button>
          </form>
        )}
      </motion.div>
      <Suspense fallback={null}><SiteFooter /></Suspense>
    </div>
  );
};

export default Contact;
